/**
 * LoopNest Media Provider — Unsplash
 * Uses Unsplash Source API (free, no key required for image display)
 * License: Unsplash License (free for commercial use, attribution appreciated)
 * https://unsplash.com/license
 */

import { BaseMediaProvider, LICENSE_TYPES, createMediaItem } from './baseProvider';

const STYLE_QUERY_MAP = {
  'nature': 'nature landscape forest mountains',
  'urban': 'city urban architecture street',
  'cinematic-stock': 'cinematic dramatic landscape',
  'realistic-photos': 'cinematic photography portrait',
  'documentary': 'documentary authentic real people',
  'worship': 'spiritual light sky peaceful sunrise',
  'realistic-captions': 'cinematic photography',
  'realistic-beats': 'dynamic motion energy',
  'visualizer-cinematic': 'cinematic abstract light',
};

// Scene category templates for BPM-aware scene building
export const SCENE_CATEGORIES = {
  nature: [
    { keyword: 'forest landscape', desc: 'establishing landscape' },
    { keyword: 'sky clouds', desc: 'sky and clouds' },
    { keyword: 'sunlight trees', desc: 'sunlight through trees' },
    { keyword: 'river flowing water', desc: 'river flowing' },
    { keyword: 'mountain aerial', desc: 'cinematic nature aerial' },
    { keyword: 'meadow flowers', desc: 'meadow and flowers' },
    { keyword: 'ocean waves', desc: 'ocean waves' },
    { keyword: 'waterfall nature', desc: 'waterfall' },
  ],
  urban: [
    { keyword: 'city skyline night', desc: 'city skyline' },
    { keyword: 'neon street lights', desc: 'neon street' },
    { keyword: 'urban architecture', desc: 'urban architecture' },
    { keyword: 'busy street people', desc: 'busy street' },
    { keyword: 'city aerial drone', desc: 'city aerial' },
    { keyword: 'subway metro', desc: 'subway' },
    { keyword: 'rooftop city view', desc: 'rooftop view' },
  ],
  cinematic: [
    { keyword: 'dramatic sky clouds', desc: 'dramatic sky' },
    { keyword: 'cinematic landscape golden hour', desc: 'golden hour' },
    { keyword: 'epic vista mountains', desc: 'epic vista' },
    { keyword: 'fog mist forest', desc: 'misty forest' },
    { keyword: 'silhouette sunset', desc: 'silhouette sunset' },
    { keyword: 'cinematic rain', desc: 'cinematic rain' },
  ],
  worship: [
    { keyword: 'light rays sky heaven', desc: 'light rays' },
    { keyword: 'sunrise peaceful', desc: 'peaceful sunrise' },
    { keyword: 'stars night sky', desc: 'stars' },
    { keyword: 'clouds light spiritual', desc: 'spiritual clouds' },
    { keyword: 'candles warm light', desc: 'warm candlelight' },
    { keyword: 'cross church architecture', desc: 'church' },
    { keyword: 'hands prayer', desc: 'prayer' },
  ],
  documentary: [
    { keyword: 'authentic people candid', desc: 'candid people' },
    { keyword: 'landscape real world', desc: 'real landscape' },
    { keyword: 'community gathering', desc: 'community' },
    { keyword: 'storytelling portrait', desc: 'portrait' },
    { keyword: 'documentary style street', desc: 'street documentary' },
  ],
  default: [
    { keyword: 'cinematic landscape', desc: 'landscape' },
    { keyword: 'dramatic sky', desc: 'sky' },
    { keyword: 'nature peaceful', desc: 'nature' },
    { keyword: 'golden hour light', desc: 'golden hour' },
    { keyword: 'abstract motion', desc: 'abstract' },
  ],
};

export class UnsplashProvider extends BaseMediaProvider {
  constructor() {
    super({
      id: 'unsplash-free',
      label: 'Unsplash',
      license: LICENSE_TYPES?.UNSPLASH,
      requiresAttribution: true,
      baseUrl: 'https://source.unsplash.com',
      enabled: true,
    });
  }

  /**
   * Get image URL for a query using Unsplash Source API
   * @param {string} query
   * @param {number} width
   * @param {number} height
   * @param {number} seed - Use scene index as seed for variety
   * @returns {string}
   */
  getImageUrl(query, width = 1280, height = 720, seed = 0) {
    const encoded = encodeURIComponent(query);
    // Add seed to get different images for same query
    const seedParam = seed > 0 ? `&sig=${seed}` : '';
    return `https://source.unsplash.com/${width}x${height}/?${encoded}${seedParam}`;
  }

  /**
   * Get style-appropriate query modifier
   */
  getStyleQuery(styleId) {
    return STYLE_QUERY_MAP?.[styleId] || 'cinematic landscape';
  }

  /**
   * Get scene categories for a given style
   */
  getSceneCategories(styleId) {
    const map = {
      'nature': SCENE_CATEGORIES?.nature,
      'cinematic-stock': SCENE_CATEGORIES?.cinematic,
      'realistic-photos': SCENE_CATEGORIES?.cinematic,
      'urban': SCENE_CATEGORIES?.urban,
      'documentary': SCENE_CATEGORIES?.documentary,
      'worship': SCENE_CATEGORIES?.worship,
      'realistic-captions': SCENE_CATEGORIES?.cinematic,
      'realistic-beats': SCENE_CATEGORIES?.cinematic,
      'visualizer-cinematic': SCENE_CATEGORIES?.cinematic,
    };
    return map?.[styleId] || SCENE_CATEGORIES?.default;
  }

  async search(query, options = {}) {
    const { width = 1280, height = 720, count = 5, seed = 0 } = options;
    const items = [];
    for (let i = 0; i < count; i++) {
      items?.push(createMediaItem({
        id: `unsplash-${query}-${i}-${seed}`,
        url: this.getImageUrl(query, width, height, seed + i),
        previewUrl: this.getImageUrl(query, 640, 360, seed + i),
        alt: query,
        providerId: this.id,
        license: this.license,
        requiresAttribution: this.requiresAttribution,
        width,
        height,
      }));
    }
    return items;
  }
}

export const unsplashProvider = new UnsplashProvider();
